import { serverState, onServerRecovered } from "@/utils/serverHealth";

/**
 * livePoll — یک نظرسنجی (polling) سبک و مؤدب برای به‌روزرسانی زنده.
 *
 * چرا polling؟ بک‌اند فعلی کانال push اختصاصی برای تیکت ندارد. این helper
 * تا زمانی که چنین کانالی اضافه شود، «بلادرنگ بودن» را با هزینه‌ی کم شبیه‌سازی می‌کند:
 *
 *   - وقتی تب مخفی است (document.hidden) هیچ درخواستی نمی‌زند (فقط تایمر سبک نگه می‌دارد).
 *   - وقتی مرورگر آفلاین است (navigator.onLine === false) هم هیچ درخواستی نمی‌زند و با
 *     رویداد online دوباره فعال می‌شود. علت اصلی مصرف CPU در حالت آفلاین همین بود:
 *     هر تیک یک fetch شکست‌خورده از مسیر Service Worker + یک رندر مجدد تولید می‌کرد.
 *   - با بازگشت فوکوس/نمایان‌شدن تب/آنلاین‌شدن، بلافاصله یک بار تازه‌سازی می‌کند.
 *   - در صورت خطای شبکه، فاصله‌ها را نمایی زیاد می‌کند (backoff) و با اولین موفقیت ریست می‌کند.
 *   - وقتی وب‌سرویس قطع است (serverState.down) تیک نمی‌زند و با برگشت سرور فوراً تازه‌سازی می‌کند.
 *   - poke() از بیرون یک تازه‌سازی فوری را اجرا می‌کند.
 *
 * @param {() => (Promise<any>|any)} fn کاری که در هر تیک اجرا می‌شود.
 * @param {{ interval?: number, minInterval?: number, maxInterval?: number }} [opts]
 */
export function createLivePoll(fn, opts) {
    const interval = (opts && opts.interval) || 20000;
    const minInterval = (opts && opts.minInterval) || 7000;
    const maxInterval = (opts && opts.maxInterval) || 120000;

    let timer = null;
    let stopped = true;
    let running = false;
    let current = interval;

    const isHidden = () => typeof document !== "undefined" && document.hidden;
    const isOffline = () =>
        typeof navigator !== "undefined" && navigator.onLine === false;

    // سرور قطع → تیک نزن؛ serverHealth خودش probe می‌کند و با برگشت سرور onWake صدا زده می‌شود.
    const canRun = () =>
        !stopped && !running && !isHidden() && !isOffline() && !serverState.down;
    let offRecovered = null;

    function schedule() {
        clearTimeout(timer);
        if (!stopped) timer = setTimeout(tick, current);
    }

    async function tick() {
        if (!canRun()) {
            // مخفی یا آفلاین → کاری نکن؛ فقط دوباره سر بزن. هیچ fetch/رندری اینجا نیست.
            schedule();
            return;
        }
        running = true;
        try {
            await fn();
            current = interval; // موفق → فاصله‌ی عادی
        } catch (e) {
            current = Math.min(current * 2, maxInterval); // خطا → backoff
        } finally {
            running = false;
            schedule();
        }
    }

    function onWake() {
        if (stopped || isHidden() || isOffline() || serverState.down) return;
        current = minInterval; // بعد از بیداری سریع‌تر چک کن
        tick();
    }

    return {
        start() {
            if (!stopped) return;
            stopped = false;
            current = interval;
            if (typeof document !== "undefined") {
                document.addEventListener("visibilitychange", onWake);
            }
            if (typeof window !== "undefined") {
                window.addEventListener("focus", onWake);
                window.addEventListener("online", onWake);
            }
            offRecovered = onServerRecovered(onWake);
            schedule();
        },
        stop() {
            stopped = true;
            clearTimeout(timer);
            if (typeof document !== "undefined") {
                document.removeEventListener("visibilitychange", onWake);
            }
            if (typeof window !== "undefined") {
                window.removeEventListener("focus", onWake);
                window.removeEventListener("online", onWake);
            }
            if (offRecovered) offRecovered();
            offRecovered = null;
        },
        /** تازه‌سازی فوری از بیرون. */
        poke() {
            if (stopped || isHidden() || isOffline() || serverState.down) return;
            current = minInterval;
            tick();
        },
    };
}

export default { createLivePoll };
